import React, { useMemo } from 'react'

import { WidgetComponent } from 'features/views/LayoutEditor/types'

import { DownloadAttachmentsButton } from 'v2/ui/components/Attribute/DownloadAttachmentsButton'
import Gallery from 'v2/ui/components/Gallery'

import { Box } from 'ui/components/Box'
import { Button } from 'ui/components/Button'
import { Icon } from 'ui/components/Icon'
import { Skeleton } from 'ui/components/Skeleton'
import { Body } from 'ui/components/Text'

import { useGalleryWidgetImageState } from './hooks/useGalleryWidgetImageState'
import { useGalleryWidgetState } from './hooks/useGalleryWidgetState'
import { GalleryWidgetType } from './types'

import {
    GalleryWidgetImageStyle,
    GalleryWidgetImageWrapperStyles,
    GalleryWidgetLayoutStyles,
    GalleryWidgetOverflowButtonStyle,
    GalleryWidgetStyle,
} from './GalleryWidget.css'

type GalleryWidgetProps = {}

const GalleryWidgetComponent: WidgetComponent<GalleryWidgetType, GalleryWidgetProps> = ({
    widget,
    isEditing,
}) => {
    const { style, allowFullImagePreview } = widget.attrs
    const {
        isLoading,
        images,
        aspectRatio,
        downloadArchiveName,
        downloadArchiveFiles,
        visibleImageCount,
        showOverflowButton,
        onOverflowButtonClick,
        containerRef,
        overflowButtonLabel,
        overflowButtonSize,
        imageSelector,
    } = useGalleryWidgetState(widget)

    // This is to prevent the gallery from injecting its own styles.
    const overrideStyle = useMemo(() => ({}), [])

    if (images.length < 1 && isEditing) {
        return (
            <Box flex column gap="m" py="l">
                <Body color="textWeakest">No images to display</Body>
            </Box>
        )
    }

    if (images.length < 1) return null

    return (
        <Box width="full" className={GalleryWidgetStyle} ref={containerRef}>
            <Box
                as={
                    (allowFullImagePreview ? Gallery : 'div') as React.ElementType<
                        React.ComponentProps<typeof Gallery>
                    >
                }
                className={GalleryWidgetLayoutStyles.styleFunction({
                    style,
                    visibleImageCount,
                })}
                style={overrideStyle}
                selector={imageSelector}
                toolbarChildren={
                    <DownloadAttachmentsButton
                        attachments={downloadArchiveFiles}
                        fieldName={downloadArchiveName}
                    />
                }
            >
                {images.map((image) => (
                    <GalleryWidgetImage
                        key={image.src}
                        src={image.src}
                        alt={image.alt}
                        isLoading={isLoading}
                        isEditing={isEditing}
                        aspectRatio={aspectRatio}
                        allowFullImagePreview={allowFullImagePreview}
                        gridArea={image.gridArea}
                        exceedsLimit={image.exceedsLimit}
                        {...image.attributes}
                    />
                ))}
                {showOverflowButton && (
                    <Box className={GalleryWidgetOverflowButtonStyle}>
                        <Button
                            size={overflowButtonSize}
                            variant="secondary"
                            startIcon={{ name: 'LayoutGrid' }}
                            onClick={onOverflowButtonClick}
                        >
                            {overflowButtonLabel}
                        </Button>
                    </Box>
                )}
            </Box>
        </Box>
    )
}

export const GalleryWidget = React.memo(GalleryWidgetComponent)

type GalleryWidgetImageProps = React.ComponentPropsWithoutRef<typeof Box> & {
    src: string
    alt: string
    isLoading?: boolean
    isEditing?: boolean
    aspectRatio?: GalleryWidgetType['attrs']['aspectRatio']
    gridArea?: string
    exceedsLimit?: boolean
}

const GalleryWidgetImage: React.FC<GalleryWidgetImageProps> = ({
    src,
    alt,
    isLoading,
    isEditing,
    aspectRatio,
    allowFullImagePreview,
    style,
    gridArea,
    exceedsLimit,
    ...props
}) => {
    const { showPlaceholder, isLoaded, onLoad, onError } = useGalleryWidgetImageState({ src })

    return (
        <Box
            className={GalleryWidgetImageWrapperStyles.styleFunction({
                aspectRatio,
                exceedsLimit,
            })}
            pointerEvents={isEditing ? 'none' : undefined}
            style={{
                gridArea,
                ...style,
            }}
            cursor={allowFullImagePreview ? 'pointer' : undefined}
            {...props}
        >
            {showPlaceholder ? (
                <Box pointerEvents="none">
                    <Icon name="ImageOff" color="icon" size="l" />
                    {/* This is a hack to make the gallery show a placeholder image. */}
                    <img
                        src="https://img.icons8.com/ios/sm/000000/document.png"
                        style={{
                            display: 'none',
                        }}
                    />
                </Box>
            ) : (
                <Skeleton display="block" width="full" height="full" isLoading={isLoading}>
                    <Box
                        as="img"
                        className={GalleryWidgetImageStyle}
                        src={src}
                        alt={alt}
                        onLoad={onLoad}
                        onError={onError}
                        opacity={isLoaded ? 1 : 0}
                        pointerEvents="none"
                    />
                </Skeleton>
            )}
        </Box>
    )
}
